import { useUploadFiles } from "@better-upload/client";
import { captureException } from "@sentry/react";
import { useCallback, useEffect, useState } from "react";
import { SENTRY_ENABLED } from "@/lib/sentry";
import { authStore } from "@/lib/stores/auth-store";

export type UploadedFile = {
	fileName: string;
	fileSize: number;
	fileType: string;
	objectKey: string;
	url: string;
};

type UseFileUploadOptions = {
	channelId: string;
	authorId: string;
	onUploadComplete?: (files: UploadedFile[]) => void;
	onError?: (error: Error) => void;
};

export function useFileUpload({
	channelId,
	authorId,
	onUploadComplete,
	onError,
}: UseFileUploadOptions) {
	const [pendingFiles, setPendingFiles] = useState<UploadedFile[]>([]);
	const [isUploading, setIsUploading] = useState(false);
	const [authToken, setAuthToken] = useState<string | null>(null);

	useEffect(() => {
		const getToken = async () => {
			const user = authStore.getState().user;
			if (user) {
				try {
					const token = await user.getIdToken();
					setAuthToken(token);
				} catch (error) {
					console.error("[Upload] Failed to get auth token:", error);
					if (SENTRY_ENABLED)
						captureException(error, {
							tags: { context: "upload_auth_token" },
						});
					setAuthToken(null);
				}
			} else {
				setAuthToken(null);
			}
		};
		getToken();

		const unsubscribe = authStore.subscribe((state) => {
			if (state.user) {
				state.user
					.getIdToken()
					.then(setAuthToken)
					.catch(() => setAuthToken(null));
			} else {
				setAuthToken(null);
			}
		});

		return unsubscribe;
	}, []);

	const { upload: baseUpload, control } = useUploadFiles({
		route: "attachments",
		api: `${import.meta.env.VITE_SERVER_URL}/api/upload`,
		headers: authToken
			? {
					Authorization: `Bearer ${authToken}`,
				}
			: undefined,
		onUploadBegin: () => {
			setIsUploading(true);
		},
		onUploadComplete: ({ metadata }) => {
			setIsUploading(false);
			const uploadedFiles = metadata?.uploadedFiles as
				| UploadedFile[]
				| undefined;
			if (uploadedFiles && uploadedFiles.length > 0) {
				setPendingFiles((prev) => [...prev, ...uploadedFiles]);
				onUploadComplete?.(uploadedFiles);
			}
		},
		onUploadFail: ({ failedFiles }) => {
			setIsUploading(false);
			if (failedFiles.length > 0) {
				onError?.(new Error(`Failed to upload ${failedFiles.length} file(s)`));
			}
		},
		onError: (error) => {
			setIsUploading(false);
			onError?.(error instanceof Error ? error : new Error(String(error)));
		},
	});

	const upload = useCallback(
		async (files: File[] | FileList) => {
			const fileArray = Array.from(files);
			for (const file of fileArray) {
				await baseUpload([file], {
					metadata: {
						channelId,
						authorId,
					},
				});
			}
		},
		[baseUpload, channelId, authorId]
	);

	const clearPendingFiles = () => {
		setPendingFiles([]);
	};

	const removePendingFile = (objectKey: string) => {
		setPendingFiles((prev) => prev.filter((f) => f.objectKey !== objectKey));
	};

	return {
		upload,
		control,
		pendingFiles,
		isUploading,
		clearPendingFiles,
		removePendingFile,
	};
}
